import { createHashRouter, defer, Navigate } from 'react-router-dom';
import ChatroomListLayout from './chatroom/chatroom-list/ChatroomListLayout';
import React from 'react';
import { chatroomListLayoutLoader } from './loaders/chatroomListLayoutLoader';
import ActivityDashboardGuard from './guards/ActivityDashboardGuard';
import ActivityDashboard from './chatroom/manage/ActivityDashboard';
import MemberDetail from './chatroom/manage/member-detail/MemberDetail';
import ManageContainer from './chatroom/manage/ManageContainer';
import CreateChatroom from './chatroom/create-chatroom/CreateChatroom';
import ShareChatroom from './chatroom/manage/ShareChatroom';
import { getActivityTemplates } from './utils';
import ChatroomListGuard from './guards/ChatroomListGuard';
import AdminContainer from './admin/AdminContainer';
import Login from './admin/Login';
import ErrorBoundary from './chatroom/ErrorBoundary';
import TemplateList from './admin/TemplateList';
import AdminUserProvider from './AdminUserProvider';
import AdminPageGuard from './guards/AdminPageGuard';
import adminService from './utils/api-calls/adminService';
import TemplateSourceGuard from './guards/TemplateSourceGuard';
import TemplateDetailContainer from './admin/TemplateDetailContainer';
import MyAnswerContainer from './chatroom/MyAnswerContainer';
import TemplateIntroduction from './chatroom/template-intro/TemplateIntroduction';
import ChatroomLandingPage from './chatroom/ChatroomLandingPage';
import axios from 'axios';
import { constants } from './constants';
import { Intro } from './models';

export const chatroomListRouter = createHashRouter([
  {
    path: '/chatrooms',
    element: <ChatroomListLayout />,
    loader: chatroomListLayoutLoader,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/chatrooms/create',
    element: <CreateChatroom />,
    loader: async () => getActivityTemplates(),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/template/:id',
    element: <TemplateIntroduction />,
    loader: ({ params }) => {
      const token = localStorage.getItem('token');
      const data = axios
        .get<{ result: Intro[] }>(
          `${constants.endpoint}/activities/template/${params.id}/intro`,
          {
            headers: {
              Authorization: `bearer ${token}`,
            },
          }
        )
        .then((res) => res.data.result);
      return defer({ data });
    },
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/chatrooms/:id',
    element: <ManageContainer />,
    children: [
      {
        index: true,
        element: (
          <ActivityDashboardGuard isChildActivityDashboard={false}>
            <ActivityDashboard isChildActivityDashboard={false} />
          </ActivityDashboardGuard>
        ),
      },
      {
        path: 'members/:memberId',
        element: <MemberDetail />,
      },
      {
        path: 'share',
        element: <ShareChatroom />,
      },
      {
        path: 'child',
        element: (
          <ActivityDashboardGuard isChildActivityDashboard={true}>
            <ActivityDashboard isChildActivityDashboard={true} />
          </ActivityDashboardGuard>
        ),
      },
    ],
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/chatrooms/:id/me',
    element: <MyAnswerContainer />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/chatrooms/:id/landing',
    element: <ChatroomLandingPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/loading',
    element: <Navigate to="/chatrooms" replace={true} />,
    errorElement: <ErrorBoundary />,
  },
  {
    // For some reason, "/" won't match "*" on LIFF browser.
    path: '/',
    element: <ChatroomListGuard />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '*',
    element: <ChatroomListGuard />,
    errorElement: <ErrorBoundary />,
  },
]);

export const adminRouter = createHashRouter([
  {
    path: 'admin',
    element: (
      <AdminUserProvider>
        <AdminContainer />
      </AdminUserProvider>
    ),
    children: [
      {
        index: true,
        loader: () => {
          return defer({ templates: adminService.getActivityTemplates() });
        },
        element: (
          <AdminPageGuard>
            <TemplateList />
          </AdminPageGuard>
        ),
      },
      {
        path: 'new',
        loader: ({ request }) => {
          const url = new URL(request.url);
          const copySourceId = url.searchParams.get('copyFrom');

          let tasks = new Promise((resolve) => resolve([]));
          let intros = new Promise((resolve) => resolve([]));
          let tagOptions = adminService.getTemplateTags(false);
          if (copySourceId) {
            tasks = adminService.getTemplateTasks(copySourceId);
            intros = adminService.getTemplateIntros(copySourceId);
          }

          return defer({
            result: Promise.all([tasks, intros, tagOptions]).then(
              ([tasks, intros, tagOptions]) => ({
                tasks,
                intros,
                tagOptions,
              })
            ),
          });
        },
        element: (
          <AdminPageGuard>
            <TemplateDetailContainer
              send={(d) => adminService.createTemplate(d)}
              refreshTags={() => adminService.getTemplateTags(true)}
            />
          </AdminPageGuard>
        ),
      },
      {
        path: 'edit/:id',
        loader: ({ params }) => {
          const tasks = adminService.getTemplateTasks(params.id!);
          const intros = adminService.getTemplateIntros(params.id!);
          const tagOptions = adminService.getTemplateTags(false);
          return defer({
            result: Promise.all([tasks, intros, tagOptions]).then(
              ([tasks, intros, tagOptions]) => ({
                tasks,
                intros,
                tagOptions,
              })
            ),
          });
        },
        element: (
          <AdminPageGuard>
            <TemplateSourceGuard>
              <TemplateDetailContainer
                send={(d) => adminService.updateTemplate(d)}
                refreshTags={() => adminService.getTemplateTags(true)}
              />
            </TemplateSourceGuard>
          </AdminPageGuard>
        ),
      },
      {
        path: 'login',
        element: <Login />,
      },
    ],
  },
  {
    errorElement: <div>Page Not Found</div>,
  },
]);
